import { useMemo, useState } from 'react';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';
import { useSessao, podeAtuar } from '../lib/session';
import { AsyncView, Button, Card, Chip, Input, Select, Stat, Table, useToast } from '../ui';
import { Drawer } from '../ui/Drawer';

type OperationStatus='delivered'|'review'|'failed'|'processing';
interface Operation {
  id:string;kind:'CRM_EVENT'|'FINANCE_DOCUMENT'|'KNOWLEDGE_ARTIFACT';source:string;destination:string;reference:string;
  type:string;status:OperationStatus;summary:string;error:string|null;attempts:number;occurredAt:string;
  processedAt:string|null;lastRetriedAt:string|null;lastRetriedBy:string|null;retryable:boolean;
  nextAction:string;
}
interface OperationResponse {items:Operation[];total:number;stats:Record<OperationStatus,number>}

const STATUS:Record<OperationStatus,{label:string;tone:'success'|'warning'|'danger'|'neutral'}>={
  delivered:{label:'Concluído',tone:'success'},review:{label:'Conflito',tone:'warning'},
  failed:{label:'Falhou',tone:'danger'},processing:{label:'Processando',tone:'neutral'},
};

export function IntegrationMonitor(){
  const {agent}=useSessao();
  const toast=useToast();
  const allowed=podeAtuar(agent.role,'ADMIN');
  const [source,setSource]=useState('all');
  const [status,setStatus]=useState('all');
  const [search,setSearch]=useState('');
  const [selected,setSelected]=useState<Operation|null>(null);
  const [retrying,setRetrying]=useState<string|null>(null);
  const path=useMemo(()=>{
    const params=new URLSearchParams({source,status,search,take:'100'});
    return `/integrations/operations?${params}`;
  },[source,status,search]);
  const {state,recarregar}=useResource<OperationResponse>(path,{enabled:allowed,disabledReason:'O monitor de integrações é exclusivo para administradores.'});

  async function retry(item:Operation){
    setRetrying(item.id);
    try{
      const resource=item.kind==='KNOWLEDGE_ARTIFACT'?'knowledge':'crm';
      await api(`/integrations/operations/${resource}/${item.id}/retry`,{method:'POST'});
      toast('Evento reprocessado. O resultado já foi atualizado.');
      setSelected(null);recarregar();
    }catch(error){toast(error instanceof ApiError?error.message:'Não foi possível reprocessar o evento.','danger');}
    finally{setRetrying(null);}
  }

  return <div style={{display:'grid',gap:'var(--space-4)'}}>
    {state.status==='ready'&&<div className="stats">
      <Stat rotulo="Concluídos" valor={state.data.stats.delivered}/>
      <Stat rotulo="Em processamento" valor={state.data.stats.processing}/>
      <Stat rotulo="Conflitos" valor={state.data.stats.review}/>
      <Stat rotulo="Falhas" valor={state.data.stats.failed}/>
    </div>}
    <Card title="Falhas e conflitos" subtitle="Acompanhe CRM, sistema financeiro e entrega de documentos ao Knowledge." actions={<Button variant="secondary" size="sm" icon={<RefreshCw size={16}/>} onClick={recarregar}>Atualizar</Button>}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:'var(--space-3)',marginBlockEnd:'var(--space-4)'}}>
        <Input label="Buscar" value={search} onChange={(e)=>setSearch(e.currentTarget.value)} placeholder="Contato, evento ou referência"/>
        <Select label="Origem" value={source} onChange={(e)=>setSource(e.currentTarget.value)} options={[{value:'all',label:'Todas'},{value:'crm',label:'CRM'},{value:'finance',label:'Financeiro'},{value:'knowledge',label:'Knowledge'}]}/>
        <Select label="Estado" value={status} onChange={(e)=>setStatus(e.currentTarget.value)} options={[{value:'all',label:'Todos'},{value:'failed',label:'Falhou'},{value:'review',label:'Conflito'},{value:'processing',label:'Processando'},{value:'delivered',label:'Concluído'}]}/>
      </div>
      <AsyncView state={state} label="as operações de integração" empty={{title:'Nenhuma operação encontrada',text:'Não há eventos que correspondam aos filtros selecionados.'}}>
        {(data)=><Table caption="Operações das integrações" rowKey={(item)=>`${item.kind}:${item.id}`} rows={data.items} columns={[
          {key:'when',header:'Data',cell:(item)=>new Date(item.occurredAt).toLocaleString('pt-BR')},
          {key:'flow',header:'Fluxo',cell:(item)=><div><strong>{item.source}</strong><br/><span style={{color:'var(--text-muted)'}}>para {item.destination}</span></div>},
          {key:'item',header:'Evento',cell:(item)=><div>{item.summary}<br/><span style={{color:'var(--text-muted)'}}>{item.type}</span></div>},
          {key:'status',header:'Estado',cell:(item)=><Chip tone={STATUS[item.status].tone}>{STATUS[item.status].label}</Chip>},
          {key:'attempts',header:'Tentativas',cell:(item)=>String(item.attempts)},
          {key:'actions',header:'Ações',align:'end',cell:(item)=><Button variant="tertiary" size="sm" onClick={()=>setSelected(item)}>Detalhes</Button>},
        ]}/>} 
      </AsyncView>
    </Card>
    <Drawer open={selected!==null} onClose={()=>setSelected(null)} title="Detalhes da operação" description="Diagnóstico e histórico do item selecionado" footer={selected&&<>
      <Button variant="tertiary" onClick={()=>setSelected(null)}>Fechar</Button>
      {selected.retryable&&<Button variant="primary" loading={retrying===selected.id} icon={<RotateCcw size={17}/>} onClick={()=>void retry(selected)}>Reprocessar</Button>}
    </>}>
      {selected&&<dl style={{display:'grid',gridTemplateColumns:'140px 1fr',gap:'var(--space-3)',margin:0}}>
        <dt>Estado</dt><dd style={{margin:0}}><Chip tone={STATUS[selected.status].tone}>{STATUS[selected.status].label}</Chip></dd>
        <dt>Origem</dt><dd style={{margin:0}}>{selected.source}</dd>
        <dt>Destino</dt><dd style={{margin:0}}>{selected.destination}</dd>
        <dt>Tipo</dt><dd style={{margin:0}}>{selected.type}</dd>
        <dt>Referência</dt><dd style={{margin:0,overflowWrap:'anywhere'}}>{selected.reference}</dd>
        <dt>Tentativas</dt><dd style={{margin:0}}>{selected.attempts}</dd>
        <dt>Recebido</dt><dd style={{margin:0}}>{new Date(selected.occurredAt).toLocaleString('pt-BR')}</dd>
        <dt>Processado</dt><dd style={{margin:0}}>{selected.processedAt?new Date(selected.processedAt).toLocaleString('pt-BR'):'—'}</dd>
        <dt>Última tentativa</dt><dd style={{margin:0}}>{selected.lastRetriedAt?new Date(selected.lastRetriedAt).toLocaleString('pt-BR'):'—'}</dd>
        <dt>Diagnóstico</dt><dd style={{margin:0}}>{selected.error||'Nenhum erro registrado.'}</dd>
        <dt>Próxima ação</dt><dd style={{margin:0}}>{selected.nextAction}</dd>
      </dl>}
    </Drawer>
  </div>;
}
